import React, { useRef, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { UI_TIMINGS } from '../../config/appConfig';
import './CameraModal.css';

export default function PreviewItem({ src, idx, selected, onSelect, onUpload, onDelete, onMatch, uploading, boundingBox, boundingBoxes = [], showBoundingBox, immediateFaceMatch, matchFailed }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const hasTriggeredMatch = useRef(false);

  // Colors for multiple bounding boxes
  const boxColors = ['#66bb6a', '#42a5f5', '#ffa726', '#ab47bc', '#ef5350'];

  const drawBoundingBoxes = () => {
    // Use boundingBoxes array if available, otherwise fall back to single boundingBox
    const boxes = boundingBoxes.length > 0 ? boundingBoxes : (boundingBox ? [boundingBox] : []);

    if (boxes.length > 0 && showBoundingBox && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const image = imageRef.current;

      // Wait for image to have dimensions
      if (image.width === 0 || image.height === 0) return;

      const ctx = canvas.getContext('2d');

      // Set canvas size to match image display size
      canvas.width = image.width;
      canvas.height = image.height;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw each bounding box with a different color
      boxes.forEach((box, index) => {
        if (!box) return;

        // AWS Rekognition bounding box coordinates are ratios (0-1)
        const x = box.Left * canvas.width;
        const y = box.Top * canvas.height;
        const width = box.Width * canvas.width;
        const height = box.Height * canvas.height;

        // Use different color for each box
        ctx.strokeStyle = boxColors[index % boxColors.length];
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, width, height);
      });
    }
  };

  useEffect(() => {
    if (showBoundingBox) {
      drawBoundingBoxes();
    } else if (canvasRef.current) {
      // Clear canvas if bounding box is hidden
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [boundingBox, boundingBoxes, src, showBoundingBox]);

  useEffect(() => {
    // Redraw on window resize
    const handleResize = () => {
      drawBoundingBoxes();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [boundingBox, boundingBoxes, showBoundingBox]);

  useEffect(() => {
    // Trigger immediate face match when component first mounts if enabled
    // Only trigger once per preview item, and only if no bounding box exists yet
    if (immediateFaceMatch && !hasTriggeredMatch.current && !boundingBox && onMatch) {
      hasTriggeredMatch.current = true;
      // Small delay to ensure component is fully rendered
      setTimeout(() => onMatch(idx), UI_TIMINGS.MATCH_CALLBACK_DELAY);
    }
  }, [immediateFaceMatch, boundingBox, idx, onMatch]);

  return (
    <div className="preview-item-root" onClick={() => onSelect(idx)}>
      <div className={`preview-image-frame ${selected ? 'selected' : ''}`}>
        <div className="preview-image-wrapper">
          <img
            ref={imageRef}
            src={src}
            alt={`capture-${idx}`}
            className="preview-image"
            onLoad={drawBoundingBoxes}
            onClick={(e) => {
              if (selected) {
                e.stopPropagation();
                onMatch(idx);
              }
            }}
            style={{ cursor: selected ? 'pointer' : 'default' }}
          />
          {boundingBox && showBoundingBox && (
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            />
          )}
          {boundingBox && (
            <AutoAwesomeIcon
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#ffa000',
                fontSize: 32,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                pointerEvents: 'none',
                zIndex: 10
              }}
            />
          )}

          {uploading && selected && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 3
              }}
            >
              <CircularProgress size={60} sx={{ color: 'white' }} />
            </Box>
          )}

          {matchFailed && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '141.42%',
                  height: '4px',
                  backgroundColor: 'red',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  transformOrigin: 'center'
                }}
              />
            </Box>
          )}

          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
            sx={{
              position: 'absolute',
              bottom: '4px',
              right: '4px',
              zIndex: 2,
              backgroundColor: 'rgba(255,255,255,0.85)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
              }
            }}
            aria-label="delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
